/**
 * Campaign Lobby
 *
 * The "out of game" landing page where players see their campaigns,
 * select one to enter, or create new ones. Shows campaign cards with
 * banner images in an ornate medieval layout.
 */

import { useState, useEffect } from 'react';
import { useCampaignStore, Campaign } from '../stores/campaignStore';
import { useAuthStore, getUserDisplayName } from '../stores/authStore';
import { GouldCorner, StormbringerIcon, KnotworkBorder } from './Ornaments';
import { Icon } from './Icon';
import { CampaignWizard } from './CampaignWizard';
import { ProfilePage } from './profile/ProfilePage';
import { TownCrier } from './TownCrier';
import { AdminPanel, isAdmin } from './admin/AdminPanel';
import { MACHINA_VERSION, DEFAULT_SOLO_AI_CONFIG } from '@machina/core';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

interface CampaignLobbyProps {
  onEnterCampaign: (campaignId: string) => void;
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

// Placeholder images for campaigns without banners
const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800&h=450&fit=crop', // dark fantasy castle
  'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&h=450&fit=crop', // mountains
  'https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0?w=800&h=450&fit=crop', // dark forest
  'https://images.unsplash.com/photo-1504701954957-2010ec3bcec1?w=800&h=450&fit=crop', // dungeon-like
];

function getPlaceholderImage(campaignId: string): string {
  // Deterministic selection based on campaign ID
  const hash = campaignId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return PLACEHOLDER_IMAGES[hash % PLACEHOLDER_IMAGES.length];
}

export function CampaignLobby({ onEnterCampaign }: CampaignLobbyProps) {
  const { user, signOut } = useAuthStore();
  const { campaigns, loadCampaigns, isLoading } = useCampaignStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showTownCrier, setShowTownCrier] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [hoveredCampaign, setHoveredCampaign] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const displayName = getUserDisplayName(user);

  useEffect(() => {
    if (user?.id) {
      loadCampaigns(user.id).finally(() => setReady(true));
    }
  }, [user?.id, loadCampaigns]);

  const dmCampaigns = campaigns.filter((c) => c.role === 'dm');
  const playerCampaigns = campaigns.filter((c) => c.role === 'player');

  return (
    <div style={{
      ...styles.container,
      opacity: ready ? 1 : 0,
      transition: 'opacity 0.4s ease',
    }}>
      {/* Decorative background pattern */}
      <div style={styles.bgPattern} />

      {/* Header */}
      <header style={styles.header}>
        <GouldCorner size={28} style={{ position: 'absolute', top: 8, left: 8 }} />
        <GouldCorner size={28} style={{ position: 'absolute', top: 8, right: 8, transform: 'scaleX(-1)' }} />

        {/* Beta stamp - old timey ink stamp look */}
        <div style={{
          position: 'absolute',
          top: 8,
          right: 36,
          background: 'transparent',
          color: '#8b1a1a',
          padding: '3px 10px',
          fontSize: '11px',
          fontWeight: 800,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          transform: 'rotate(8deg)',
          border: '3px solid #8b1a1a',
          borderRadius: '4px',
          fontFamily: 'var(--font-display)',
          zIndex: 10,
          opacity: 0.85,
          textShadow: '0 0 1px #8b1a1a',
          boxShadow: `
            inset 0 0 0 1px rgba(139, 26, 26, 0.3),
            0 0 0 1px rgba(139, 26, 26, 0.1)
          `,
          filter: 'saturate(0.9) contrast(1.1)',
        }}>
          BETA
        </div>

        <div style={styles.headerLeft}>
          <StormbringerIcon size={40} />
          <div>
            <h1 style={styles.title}>MACHINA</h1>
            <p style={styles.subtitle}>Virtual Tabletop Powered by The Deep</p>
          </div>
        </div>

        <div style={styles.headerRight}>
          {/* Admin button - only visible to admins */}
          {isAdmin(user?.email) && (
            <button
              onClick={() => setShowAdminPanel(true)}
              title="Admin Panel"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
                padding: 'var(--spacing-xs) var(--spacing-sm)',
                backgroundColor: 'transparent',
                border: `1px solid ${GOLD}`,
                borderRadius: 'var(--radius-sm)',
                color: GOLD,
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Icon name="crown" size={14} />
              Admin
            </button>
          )}

          {/* Town Crier button - Scroll with wooden spools */}
          <div
            onClick={() => setShowTownCrier(true)}
            title="What's New in Machina"
            className="scroll-button"
            style={{
              width: 36,
              height: 46,
              padding: 0,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              transform: 'rotate(-12deg)',
              filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.5))',
            }}
          >
            <style>{`
              .scroll-button {
                transition: transform 0.2s ease, filter 0.2s ease;
              }
              .scroll-button:hover {
                transform: rotate(-12deg) scale(1.1);
                filter: drop-shadow(0 5px 10px rgba(212, 175, 55, 0.5));
              }
              .scroll-button .parchment-body {
                transition: height 0.3s ease;
              }
              .scroll-button:hover .parchment-body {
                height: 28px;
              }
              .scroll-button .bottom-spool {
                transition: transform 0.3s ease;
              }
              .scroll-button:hover .bottom-spool {
                transform: translateY(10px);
              }
              .scroll-button .text-lines {
                transition: opacity 0.3s ease 0.1s;
                opacity: 0;
              }
              .scroll-button:hover .text-lines {
                opacity: 0.6;
              }
            `}</style>

            <svg width="36" height="46" viewBox="0 0 36 46" fill="none" style={{ overflow: 'visible' }}>
              <defs>
                <linearGradient id="woodGrainLobby" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8B4513" />
                  <stop offset="20%" stopColor="#A0522D" />
                  <stop offset="40%" stopColor="#8B4513" />
                  <stop offset="60%" stopColor="#A0522D" />
                  <stop offset="80%" stopColor="#8B4513" />
                  <stop offset="100%" stopColor="#6B3510" />
                </linearGradient>
                <radialGradient id="woodEndLobby">
                  <stop offset="0%" stopColor="#D2691E" />
                  <stop offset="60%" stopColor="#8B4513" />
                  <stop offset="100%" stopColor="#5D3A1A" />
                </radialGradient>
                <linearGradient id="parchmentFillLobby" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#e8d4a8" />
                  <stop offset="10%" stopColor="#f4e4bc" />
                  <stop offset="90%" stopColor="#f4e4bc" />
                  <stop offset="100%" stopColor="#d4c094" />
                </linearGradient>
              </defs>

              {/* Top wooden spool */}
              <g className="top-spool">
                <ellipse cx="4" cy="5" rx="3" ry="4" fill="url(#woodEndLobby)" />
                <rect x="4" y="1" width="28" height="8" rx="1" fill="url(#woodGrainLobby)" />
                <ellipse cx="32" cy="5" rx="3" ry="4" fill="url(#woodEndLobby)" />
                <line x1="10" y1="2" x2="10" y2="8" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                <line x1="18" y1="2" x2="18" y2="8" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                <line x1="26" y1="2" x2="26" y2="8" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
              </g>

              {/* Parchment paper */}
              <rect className="parchment-body" x="7" y="9" width="22" height="18" rx="1" fill="url(#parchmentFillLobby)" />

              {/* Text lines */}
              <g className="text-lines">
                <line x1="11" y1="14" x2="25" y2="14" stroke="#8B7355" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="11" y1="18" x2="23" y2="18" stroke="#8B7355" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="11" y1="22" x2="21" y2="22" stroke="#8B7355" strokeWidth="1.5" strokeLinecap="round" />
              </g>

              {/* Bottom wooden spool */}
              <g className="bottom-spool">
                <ellipse cx="4" cy="31" rx="3" ry="4" fill="url(#woodEndLobby)" />
                <rect x="4" y="27" width="28" height="8" rx="1" fill="url(#woodGrainLobby)" />
                <ellipse cx="32" cy="31" rx="3" ry="4" fill="url(#woodEndLobby)" />
                <line x1="10" y1="28" x2="10" y2="34" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                <line x1="18" y1="28" x2="18" y2="34" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
                <line x1="26" y1="28" x2="26" y2="34" stroke="#6B3510" strokeWidth="0.5" opacity="0.5" />
              </g>
            </svg>
          </div>

          <button onClick={() => setShowProfile(true)} style={styles.userButton}>
            {displayName}
          </button>
          <button onClick={signOut} style={styles.signOutButton}>
            Sign Out
          </button>
        </div>
      </header>

      {/* Main content */}
      <main style={styles.main}>
        {/* Welcome section */}
        <div style={styles.welcomeSection}>
          <KnotworkBorder size={300} style={{ opacity: 0.3 }} />
          <h2 style={styles.welcomeTitle}>Welcome, {displayName}</h2>
          <p style={styles.welcomeText}>Choose a campaign to enter, or begin a new adventure.</p>
        </div>

        {/* Campaigns grid */}
        <div style={styles.campaignsContainer}>
          {/* DM Campaigns */}
          {dmCampaigns.length > 0 && (
            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <Icon name="crown" size={18} color={GOLD} />
                Your Campaigns
              </h3>
              <div style={styles.campaignGrid}>
                {dmCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    isHovered={hoveredCampaign === campaign.id}
                    onHover={() => setHoveredCampaign(campaign.id)}
                    onLeave={() => setHoveredCampaign(null)}
                    onEnter={() => onEnterCampaign(campaign.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Player Campaigns */}
          {playerCampaigns.length > 0 && (
            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>
                <Icon name="token" size={18} color={GOLD} />
                Playing In
              </h3>
              <div style={styles.campaignGrid}>
                {playerCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    isHovered={hoveredCampaign === campaign.id}
                    onHover={() => setHoveredCampaign(campaign.id)}
                    onLeave={() => setHoveredCampaign(null)}
                    onEnter={() => onEnterCampaign(campaign.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Empty state */}
          {campaigns.length === 0 && !isLoading && (
            <div style={styles.emptyState}>
              <Icon name="castle" size={48} color={GOLD_DIM} />
              <h3 style={styles.emptyTitle}>No Adventures Yet</h3>
              <p style={styles.emptyText}>
                Create your first campaign to begin your journey.
              </p>
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div style={styles.emptyState}>
              <Icon name="slow" size={48} color={GOLD_DIM} />
              <p style={styles.emptyText}>Loading your campaigns...</p>
            </div>
          )}
        </div>

        {/* Create Campaign Card */}
        <div style={styles.createSection}>
          <button
            onClick={() => setShowCreateModal(true)}
            style={styles.createButton}
          >
            <div style={styles.createButtonInner}>
              <Icon name="plus" size={32} color={GOLD} />
              <span style={styles.createButtonText}>New Campaign</span>
            </div>
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <span>Machina v{MACHINA_VERSION}</span>
        <span style={{ opacity: 0.5 }}>•</span>
        <span>Powered by The Deep</span>
      </footer>

      {/* Modals */}
      {showCreateModal && (
        <CampaignWizard
          onClose={() => setShowCreateModal(false)}
          onCreated={(id) => {
            setShowCreateModal(false);
            onEnterCampaign(id);
          }}
        />
      )}

      {showProfile && <ProfilePage onClose={() => setShowProfile(false)} />}

      {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} />}

      <TownCrier forceShow={showTownCrier} onDismiss={() => setShowTownCrier(false)} />
    </div>
  );
}

// Campaign Card Component
interface CampaignCardProps {
  campaign: Campaign;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  onEnter: () => void;
}

function CampaignCard({ campaign, isHovered, onHover, onLeave, onEnter }: CampaignCardProps) {
  const bannerUrl = getPlaceholderImage(campaign.id);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showSoloMenu, setShowSoloMenu] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const { switchToSolo, switchToStandard } = useCampaignStore();
  const soloEnabled = useFeatureFlag('soloMode');
  const isSolo = campaign.campaignType === 'solo';
  const isDm = campaign.role === 'dm';

  const handleToggleSolo = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSwitching(true);
    if (isSolo) {
      await switchToStandard(campaign.id);
    } else {
      await switchToSolo(campaign.id, DEFAULT_SOLO_AI_CONFIG);
    }
    setIsSwitching(false);
    setShowSoloMenu(false);
  };

  return (
    <div
      style={{
        ...styles.card,
        transform: isHovered ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
        boxShadow: isHovered
          ? `0 12px 40px rgba(0, 0, 0, 0.5), 0 0 20px ${GOLD_DIM}`
          : '0 4px 20px rgba(0, 0, 0, 0.4)',
      }}
      onMouseEnter={onHover}
      onMouseLeave={() => { onLeave(); setShowSoloMenu(false); }}
      onClick={onEnter}
    >
      {/* Banner image */}
      <div style={styles.cardBanner}>
        <img
          src={bannerUrl}
          alt={campaign.name}
          style={{
            ...styles.cardImage,
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
          onLoad={() => setImageLoaded(true)}
        />
        {/* Solid placeholder while image loads */}
        {!imageLoaded && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #1a1410 0%, #2a1f18 50%, #1a1410 100%)',
          }} />
        )}
        <div style={styles.cardImageOverlay} />

        {/* Badges row */}
        <div style={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 6,
          alignItems: 'center',
        }}>
          {/* Solo badge */}
          {soloEnabled && isSolo && (
            <div style={{
              padding: '4px 10px',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              backgroundColor: '#9333ea',
              color: '#fff',
            }}>
              Solo
            </div>
          )}
          {/* Role badge */}
          <div style={{
            ...styles.roleBadge,
            position: 'static',
            backgroundColor: campaign.role === 'dm' ? GOLD : 'var(--color-surface)',
            color: campaign.role === 'dm' ? '#1a1410' : 'var(--color-text)',
          }}>
            {campaign.role === 'dm' ? 'DM' : 'Player'}
          </div>
        </div>
      </div>

      {/* Card content */}
      <div style={styles.cardContent}>
        <h4 style={styles.cardTitle}>{campaign.name}</h4>
        {campaign.description && (
          <p style={styles.cardDescription}>{campaign.description}</p>
        )}

        {/* Solo toggle (DM only, feature-flagged) */}
        {soloEnabled && isDm && (
          <div style={{ marginBottom: 8, position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setShowSoloMenu(!showSoloMenu); }}
              style={{
                padding: '3px 10px',
                borderRadius: 'var(--radius-sm)',
                border: `1px solid ${isSolo ? 'rgba(147, 51, 234, 0.4)' : 'var(--color-border)'}`,
                background: isSolo ? 'rgba(147, 51, 234, 0.1)' : 'transparent',
                color: isSolo ? '#9333ea' : 'var(--color-text-muted)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icon name="sparkle" size={12} />
              {isSolo ? 'AI DM Active' : 'Enable AI DM'}
            </button>

            {/* Confirmation popover */}
            {showSoloMenu && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  marginBottom: 6,
                  padding: 12,
                  backgroundColor: 'var(--color-surface)',
                  border: `1px solid ${isSolo ? 'rgba(147, 51, 234, 0.4)' : GOLD_DIM}`,
                  borderRadius: 'var(--radius-sm)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  zIndex: 10,
                  minWidth: 200,
                }}
              >
                <div style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text)',
                  marginBottom: 8,
                }}>
                  {isSolo
                    ? 'Switch back to standard (human DM)?'
                    : 'Enable AI Dungeon Master for this campaign?'
                  }
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowSoloMenu(false); }}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--color-border)',
                      background: 'transparent',
                      color: 'var(--color-text-muted)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleToggleSolo}
                    disabled={isSwitching}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: `1px solid ${isSolo ? '#dc143c' : '#9333ea'}`,
                      background: isSolo ? 'rgba(220, 20, 60, 0.15)' : 'rgba(147, 51, 234, 0.15)',
                      color: isSolo ? '#dc143c' : '#9333ea',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      cursor: isSwitching ? 'default' : 'pointer',
                      opacity: isSwitching ? 0.6 : 1,
                    }}
                  >
                    {isSwitching ? 'Switching...' : isSolo ? 'Disable' : 'Enable'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Enter button */}
        <button
          style={{
            ...styles.enterButton,
            opacity: isHovered ? 1 : 0.7,
          }}
        >
          <Icon name="play" size={14} />
          Enter Campaign
        </button>
      </div>

      {/* Corner ornaments */}
      <GouldCorner size={20} color={GOLD_DIM} style={{ position: 'absolute', top: 4, left: 4, opacity: 0.5 }} />
      <GouldCorner size={20} color={GOLD_DIM} style={{ position: 'absolute', top: 4, right: 4, transform: 'scaleX(-1)', opacity: 0.5 }} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-background)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },

  bgPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.02,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    pointerEvents: 'none',
  },

  header: {
    padding: 'var(--spacing-md) var(--spacing-xl)',
    backgroundColor: 'var(--color-surface)',
    borderBottom: `2px solid ${GOLD_DIM}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
  },

  title: {
    margin: 0,
    fontSize: '1.75rem',
    fontWeight: 700,
    letterSpacing: '0.2em',
    color: GOLD,
    textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
  },

  subtitle: {
    margin: 0,
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.1em',
  },

  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
  },

  iconButton: {
    width: 36,
    height: 36,
    padding: 0,
    background: `linear-gradient(135deg, #f4e4bc 0%, ${GOLD} 30%, #a08030 70%, #8b6914 100%)`,
    border: `2px solid ${GOLD}`,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 3px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.4)',
  },

  userButton: {
    background: 'none',
    border: 'none',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    cursor: 'pointer',
    padding: 'var(--spacing-xs) var(--spacing-sm)',
  },

  signOutButton: {
    padding: 'var(--spacing-xs) var(--spacing-sm)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
  },

  main: {
    flex: 1,
    padding: 'var(--spacing-xl)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },

  welcomeSection: {
    textAlign: 'center',
    marginBottom: 'var(--spacing-xl)',
    position: 'relative',
  },

  welcomeTitle: {
    margin: 0,
    fontSize: '2rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 400,
    color: 'var(--color-text)',
    letterSpacing: '0.05em',
  },

  welcomeText: {
    margin: 0,
    marginTop: 'var(--spacing-sm)',
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-text-secondary)',
  },

  campaignsContainer: {
    width: '100%',
    maxWidth: 1200,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--spacing-xl)',
    alignSelf: 'stretch',
    margin: '0 auto',
  },

  section: {
    marginBottom: 'var(--spacing-xl)',
    width: '100%',
  },

  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    margin: 0,
    marginBottom: 'var(--spacing-lg)',
    fontSize: 'var(--font-size-lg)',
    fontFamily: 'var(--font-display)',
    fontWeight: 400,
    color: GOLD,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },

  campaignGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 'var(--spacing-lg)',
    width: '100%',
    boxSizing: 'border-box',
  },

  card: {
    position: 'relative',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    border: `2px solid ${GOLD_DIM}`,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
  },

  cardBanner: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    overflow: 'hidden',
  },

  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  cardImageOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.8) 100%)',
  },

  roleBadge: {
    position: 'absolute',
    top: 12,
    right: 12,
    padding: '4px 12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  cardContent: {
    padding: 'var(--spacing-md)',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  cardTitle: {
    margin: 0,
    fontSize: 'var(--font-size-lg)',
    fontWeight: 600,
    color: 'var(--color-text)',
    marginBottom: 'var(--spacing-xs)',
  },

  cardDescription: {
    margin: 0,
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-md)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },

  enterButton: {
    width: '100%',
    marginTop: 'auto',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
    border: `2px solid ${GOLD}`,
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
  },

  emptyState: {
    textAlign: 'center',
    padding: 'var(--spacing-xl)',
  },

  emptyTitle: {
    margin: 0,
    marginTop: 'var(--spacing-md)',
    fontSize: 'var(--font-size-lg)',
    color: 'var(--color-text)',
  },

  emptyText: {
    margin: 0,
    marginTop: 'var(--spacing-sm)',
    color: 'var(--color-text-secondary)',
  },

  createSection: {
    marginTop: 'var(--spacing-xl)',
  },

  createButton: {
    padding: 'var(--spacing-lg) var(--spacing-xl)',
    backgroundColor: 'transparent',
    border: `2px dashed ${GOLD_DIM}`,
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
  },

  createButtonInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
  },

  createButtonText: {
    fontSize: 'var(--font-size-md)',
    fontFamily: 'var(--font-display)',
    color: GOLD,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },

  footer: {
    padding: 'var(--spacing-md)',
    textAlign: 'center',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    display: 'flex',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    borderTop: `1px solid var(--color-border)`,
  },
};
